/* Gunrange shop — shell behaviour and the shared framework every feature module uses.
   Vanilla JS, no build step. Loaded with `defer` on every page from layout/theme.liquid.

   window.GR (the only global):
     GR.layer.open(id, trigger?)   open the [data-gr-layer] element with this id
     GR.layer.close()              close the open layer, return focus to its trigger
     GR.layer.current              the open layer element, or null
     GR.layer.wire()               (re)attach ARIA to [data-gr-open] triggers
     GR.focusables(el)             tabbable descendants of el, in DOM order
     GR.fetchSection(url, id, opts?) render section `id` in the context of `url`
                                   (Section Rendering API); resolves to the
                                   parsed Document, rejects on a non-2xx
     GR.setTheme('light'|'dark', remember?)
     GR.template(name)             a copy of <template data-gr-tpl="name">
     GR.debounce(fn, ms)           fn, run ms after the last call; .cancel()
                                   drops a waiting call, .pending says if one waits
   Also: the disclosure toggle ([data-gr-toggle]), tabs (role=tablist, panels
   in [data-gr-tabs]), the quantity stepper ([data-gr-qty]) and the hero
   slider ([data-gr-slider], track in [data-gr-slider-track]). Markup
   contract and events: docs/build-notes.md. */
(() => {
  'use strict';

  const root = document.documentElement;
  const GR = (window.GR = window.GR || {});

  /* ── Helpers ─────────────────────────────────────────────────────────── */
  const FOCUSABLE = [
    'a[href]', 'area[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])', 'textarea:not([disabled])', 'iframe', '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  GR.focusables = (el) =>
    Array.from(el.querySelectorAll(FOCUSABLE)).filter(
      (n) => !n.closest('[inert]') && n.getClientRects().length > 0
    );

  const wide = window.matchMedia('(min-width: 900px)');

  /* ── Section Rendering ─────────────────────────────────────────────────
     One fetch for every module that re-renders server-side markup (results,
     predictive search, quick add, later the cart): the section is rendered in
     the context of `url` (its query string included), so filters, the page
     and search terms all apply. A failed render rejects; callers decide how
     to degrade (usually: navigate to `url`). */
  GR.fetchSection = async (url, sectionId, { signal } = {}) => {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('section_id', sectionId);
    const response = await fetch(target, { signal, headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error(`Section "${sectionId}" failed to render (HTTP ${response.status}) for ${url}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  };

  /* ── Debounce ──────────────────────────────────────────────────────────
     One timer per debounced function: each call restarts the wait, so fn
     runs once, with the last call's arguments, ms after the calls stop. */
  GR.debounce = (fn, ms) => {
    let timer = null;
    const debounced = (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        fn(...args);
      }, ms);
    };
    debounced.cancel = () => {
      clearTimeout(timer);
      timer = null;
    };
    Object.defineProperty(debounced, 'pending', { get: () => timer !== null });
    return debounced;
  };

  /* ── Templates ─────────────────────────────────────────────────────────
     Scripts never build markup with icons or copy: the states they show are
     server-rendered <template data-gr-tpl="name"> elements (e.g.
     snippets/gr-product-templates.liquid). GR.template(name) returns a fresh
     copy, or null (logged) when the template isn't on the page. */
  GR.template = (name) => {
    const tpl = document.querySelector(`template[data-gr-tpl="${CSS.escape(name)}"]`);
    if (!tpl) {
      console.error(`[gr-shop] Template "${name}" is missing from the page.`);
      return null;
    }
    return tpl.content.cloneNode(true);
  };

  /* ── Theme mode ──────────────────────────────────────────────────────────
     The inline script in <head> sets data-theme before first paint. The
     toggle's two states are both server-rendered; CSS shows the right one. */
  const THEME_KEY = 'gr-theme';
  const savedTheme = () => {
    try {
      const t = localStorage.getItem(THEME_KEY);
      return t === 'light' || t === 'dark' ? t : null;
    } catch (e) {
      return null; // storage blocked: behave as "no explicit choice"
    }
  };
  GR.setTheme = (theme, remember) => {
    root.setAttribute('data-theme', theme);
    if (!remember) return;
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch (e) {
      // Storage blocked (private mode, policy): the switch still applies to this page.
      console.warn('[gr-shop] Theme choice could not be saved; it applies to this page only.', e);
    }
  };
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-gr-theme-toggle]')) return;
    GR.setTheme(root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark', true);
  });
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    if (!savedTheme()) GR.setTheme(e.matches ? 'dark' : 'light', false);
  });

  /* ── Header condense (300ms, one attribute on <html>) ──────────────────── */
  let ticking = false;
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      root.toggleAttribute('data-scrolled', window.scrollY >= 24);
      ticking = false;
    });
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  /* ── Disclosure toggle ───────────────────────────────────────────────────
     <button data-gr-toggle aria-expanded="false" aria-controls="id">: flips
     aria-expanded and the target's `hidden`. Filters, accordions and the
     cookie settings all use this one. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-gr-toggle]');
    if (!btn) return;
    const open = btn.getAttribute('aria-expanded') !== 'true';
    btn.setAttribute('aria-expanded', String(open));
    const id = btn.getAttribute('aria-controls');
    const target = id ? document.getElementById(id) : null;
    if (target) target.hidden = !open;
  });

  /* ── Tabs ────────────────────────────────────────────────────────────────
     A role=tablist of <button role="tab" aria-controls="panel-id">, the
     server marking the first tab selected. Until this runs, CSS shows only
     the first panel of a [data-gr-tabs] container; here each panel gets its
     tabpanel role and label, the others `hidden`, and the container
     data-gr-ready. Switching fades the shown panel out (its CSS transition,
     200ms; none under reduced motion), then fades the next one in (handbook
     "tab crossfade"). Arrow keys, Home and End move between tabs and select
     them. In the theme editor, selecting a tab's block selects the tab. */
  const panelOf = (tab) => document.getElementById(tab.getAttribute('aria-controls'));
  const tabsOf = (list) => Array.from(list.querySelectorAll('[role="tab"]'));
  const fades = new WeakMap();

  const initTabs = () => {
    document.querySelectorAll('[role="tablist"]:not([data-gr-tabs-ready])').forEach((list) => {
      const tabs = tabsOf(list);
      const panels = tabs.map(panelOf);
      if (!tabs.length || panels.some((panel) => !panel)) {
        console.error('[gr-shop] A tab row has a tab without its panel; left as it is.', list);
        return;
      }
      tabs.forEach((tab, i) => {
        panels[i].setAttribute('role', 'tabpanel');
        panels[i].setAttribute('aria-labelledby', tab.id);
        panels[i].hidden = tab.getAttribute('aria-selected') !== 'true';
      });
      list.setAttribute('data-gr-tabs-ready', '');
      panels[0].closest('[data-gr-tabs]')?.setAttribute('data-gr-ready', '');
    });
  };

  const selectTab = (tab) => {
    const list = tab.closest('[role="tablist"]');
    const tabs = tabsOf(list);
    tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    });
    const panels = tabs.map(panelOf).filter(Boolean);
    const next = panelOf(tab);
    if (!next) return;
    const shown = panels.find((panel) => !panel.hidden && panel !== next);
    clearTimeout(fades.get(list));
    const swap = () => {
      next.setAttribute('data-fade', '');
      panels.forEach((panel) => {
        panel.hidden = panel !== next;
        if (panel !== next) panel.removeAttribute('data-fade');
      });
      next.getBoundingClientRect(); // lay out the faded start, so the fade-in runs
      next.removeAttribute('data-fade');
    };
    if (!shown) {
      swap();
      return;
    }
    shown.setAttribute('data-fade', '');
    const out = parseFloat(getComputedStyle(shown).transitionDuration) * 1000 || 0;
    fades.set(list, setTimeout(swap, out));
  };

  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-gr-tabs-ready] [role="tab"]');
    if (tab && tab.getAttribute('aria-selected') !== 'true') selectTab(tab);
  });
  document.addEventListener('keydown', (e) => {
    const tab = e.target.closest && e.target.closest('[data-gr-tabs-ready] [role="tab"]');
    if (!tab) return;
    const tabs = tabsOf(tab.closest('[role="tablist"]'));
    const at = tabs.indexOf(tab);
    const to = { ArrowRight: at + 1, ArrowLeft: at - 1, Home: 0, End: tabs.length - 1 }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    const next = tabs[(to + tabs.length) % tabs.length];
    next.focus();
    if (next !== tab) selectTab(next);
  });
  document.addEventListener('shopify:block:select', (e) => {
    const tab = e.target.id && document.querySelector(`[role="tab"][aria-controls="${CSS.escape(e.target.id)}"]`);
    if (tab) selectTab(tab);
  });

  /* ── Quantity stepper (snippets/gr-quantity.liquid) ──────────────────────
     [data-gr-qty="down|up"] step the .qty's number input within its min/max
     and fire `change` like a typed value. "down" is aria-disabled at the
     minimum (aria, not `disabled`, so a focused button keeps focus). */
  const syncQty = (input) => {
    const down = input.closest('.qty')?.querySelector('[data-gr-qty="down"]');
    if (!down) return;
    const min = input.min === '' ? 1 : Number(input.min);
    const atMin = (parseInt(input.value, 10) || min) <= min;
    if (atMin) down.setAttribute('aria-disabled', 'true');
    else down.removeAttribute('aria-disabled');
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-gr-qty]');
    if (!btn) return;
    const input = btn.closest('.qty')?.querySelector('input');
    if (!input || input.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    const min = input.min === '' ? 1 : Number(input.min);
    const max = input.max === '' ? Infinity : Number(input.max);
    const current = parseInt(input.value, 10);
    const from = Number.isFinite(current) ? current : min;
    const next = Math.min(max, Math.max(min, from + (btn.dataset.grQty === 'up' ? 1 : -1)));
    if (next === current) return;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
  document.addEventListener('change', (e) => {
    if (e.target.matches('.qty input')) syncQty(e.target);
  });

  /* ── Layers: drawers, overlays and modals share one scrim, one scroll lock,
     one focus trap. A layer is any element with id + data-gr-layer, rendered
     with `inert` while closed. Triggers are [data-gr-open="<id>"]; when the
     layer isn't on the page the trigger keeps its default (e.g. a link to
     /cart), so every trigger works before its drawer exists. ──────────────── */
  const layer = (() => {
    let current = null;
    let returnTo = null;
    const scrim = () => document.querySelector('[data-gr-scrim]');
    const triggers = (id) => document.querySelectorAll(`[data-gr-open="${CSS.escape(id)}"]`);
    const setExpanded = (id, on) => triggers(id).forEach((t) => t.setAttribute('aria-expanded', String(on)));

    const hide = (el) => {
      el.removeAttribute('data-open');
      el.inert = true;
      setExpanded(el.id, false);
      el.dispatchEvent(new CustomEvent('gr:layer-close', { bubbles: true }));
    };

    const open = (id, trigger) => {
      const el = document.getElementById(id);
      if (!el || !el.hasAttribute('data-gr-layer')) return false;
      if (current === el) return true;
      // Replacing one layer with another keeps the original return target.
      const origin = current ? returnTo : trigger || document.activeElement;
      if (current) hide(current);
      current = el;
      returnTo = origin;
      el.inert = false;
      el.setAttribute('data-open', '');
      const s = scrim();
      if (s) s.setAttribute('data-open', '');
      root.classList.add('gr-locked');
      setExpanded(id, true);
      el.dispatchEvent(new CustomEvent('gr:layer-open', { bubbles: true }));
      const first = el.querySelector('[data-gr-autofocus]') || GR.focusables(el)[0] || el;
      if (first === el && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
      requestAnimationFrame(() => first.focus({ preventScroll: true }));
      return true;
    };

    const close = () => {
      if (!current) return;
      const el = current;
      // A re-render may have replaced the trigger (e.g. the filters button): fall back to its successor.
      let target = returnTo;
      if (!target || !target.isConnected) {
        target = Array.from(triggers(el.id)).find((t) => t.getClientRects().length > 0) || null;
      }
      current = null;
      returnTo = null;
      hide(el);
      const s = scrim();
      if (s) s.removeAttribute('data-open');
      root.classList.remove('gr-locked');
      if (target && target.isConnected && typeof target.focus === 'function') {
        target.focus({ preventScroll: true });
      }
    };

    const wire = () => {
      document.querySelectorAll('[data-gr-open]').forEach((t) => {
        const id = t.getAttribute('data-gr-open');
        const el = document.getElementById(id);
        if (!el || !el.hasAttribute('data-gr-layer')) return; // stays a plain link/button
        t.setAttribute('aria-controls', id);
        t.setAttribute('aria-haspopup', 'dialog');
        t.setAttribute('aria-expanded', String(current === el));
      });
    };

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-gr-open]');
      if (opener) {
        if (open(opener.getAttribute('data-gr-open'), opener)) e.preventDefault();
        return;
      }
      // [data-gr-backdrop]: a layer whose own box is the backdrop (a modal) closes on a click on itself.
      if (current && (e.target.closest('[data-gr-close]') || e.target.matches('[data-gr-scrim], [data-gr-backdrop]'))) close();
    });

    document.addEventListener('keydown', (e) => {
      if (!current) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = GR.focusables(current);
      if (!items.length) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!current.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Back/forward cache: never restore a page with a layer frozen open.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) close();
    });

    return {
      open,
      close,
      wire,
      get current() {
        return current;
      }
    };
  })();
  GR.layer = layer;

  /* ── Category bar: mega menus + "More" overflow (desktop, ≥900) ──────────
     Click toggles; a mouse opens on hover after a 90ms intent delay. Every
     "More" column is server-rendered hidden; fit() reveals the ones whose
     category no longer fits on the bar. */
  const catnav = (() => {
    let nav = null;
    let items = [];

    const parts = (it) => ({
      btn: it.querySelector(':scope > button.catnav__btn'),
      panel: it.querySelector(':scope > .mega')
    });
    const closeAll = (except) => {
      items.forEach((it) => {
        if (it === except) return;
        const { btn, panel } = parts(it);
        if (btn) btn.setAttribute('aria-expanded', 'false');
        if (panel) panel.removeAttribute('data-open');
      });
    };
    const openItem = (it) => {
      const { btn, panel } = parts(it);
      if (!btn || !panel) return;
      closeAll(it);
      btn.setAttribute('aria-expanded', 'true');
      panel.setAttribute('data-open', '');
    };
    const openItemEl = () => items.find((it) => parts(it).panel?.hasAttribute('data-open'));

    const fit = () => {
      if (!nav) return;
      const more = nav.querySelector('[data-gr-more]');
      const cats = Array.from(nav.querySelectorAll('[data-gr-cat]'));
      if (!more || !cats.length) return;
      const cols = Array.from(more.querySelectorAll('[data-gr-more-col]'));
      cats.forEach((c) => (c.hidden = false));
      cols.forEach((c) => (c.hidden = true));
      more.hidden = true;
      if (wide.matches) {
        const fixed = Array.from(nav.querySelectorAll('.catnav__link')).reduce((a, el) => a + el.offsetWidth, 0);
        const visible = () => cats.filter((c) => !c.hidden);
        const width = () =>
          visible().reduce((a, el) => a + el.offsetWidth, 0) + (more.hidden ? 0 : more.offsetWidth);
        const room = nav.clientWidth - fixed - 24;
        if (width() > room) more.hidden = false;
        while (width() > room && visible().length > 1) {
          const v = visible();
          const last = v[v.length - 1];
          last.hidden = true;
          const col = more.querySelector(`[data-gr-more-col="${last.dataset.grCat}"]`);
          if (col) col.hidden = false;
        }
      }
      const openIt = openItemEl();
      if (openIt && openIt.hidden) closeAll();
    };

    const init = () => {
      nav = document.querySelector('[data-gr-catnav]');
      if (!nav || nav.dataset.grBound) return;
      nav.dataset.grBound = '1';
      items = Array.from(nav.querySelectorAll('.catnav__item'));
      items.forEach((it) => {
        const { btn, panel } = parts(it);
        if (!btn || !panel) return;
        btn.addEventListener('click', () => {
          if (btn.getAttribute('aria-expanded') === 'true') closeAll();
          else openItem(it);
        });
        let timer;
        it.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          timer = setTimeout(() => openItem(it), 90);
        });
        it.addEventListener('pointerleave', (e) => {
          clearTimeout(timer);
          if (e.pointerType === 'mouse') closeAll();
        });
        // Tabbing out of an open panel closes it (a click elsewhere is handled below).
        it.addEventListener('focusout', (e) => {
          if (e.relatedTarget && !it.contains(e.relatedTarget)) closeAll();
        });
      });
      fit();
    };

    document.addEventListener('click', (e) => {
      if (nav && !e.target.closest('[data-gr-catnav]')) closeAll();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || layer.current) return;
      const it = openItemEl();
      if (!it) return;
      const hadFocus = it.contains(document.activeElement);
      closeAll();
      if (hadFocus) parts(it).btn.focus();
    });
    document.addEventListener('gr:layer-open', () => closeAll());
    window.addEventListener('resize', GR.debounce(fit, 120));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);

    return { init, fit };
  })();
  GR.catnav = catnav;

  /* ── Mobile nav: three levels of panels inside the #gr-mnav drawer ──────── */
  const initMobileNav = () => {
    const nav = document.querySelector('[data-gr-mnav]');
    if (!nav || nav.dataset.grBound) return;
    nav.dataset.grBound = '1';
    const rootPanel = nav.querySelector('.mnav__panel[data-level="0"]');
    const show = (target) => {
      nav.querySelectorAll('.mnav__panel').forEach((p) => {
        const on = p === target;
        if (p !== rootPanel) p.toggleAttribute('data-active', on);
        p.inert = !on;
      });
    };
    nav.addEventListener('click', (e) => {
      const to = e.target.closest('[data-gr-mnav-to]');
      if (to) {
        const target = document.getElementById(to.dataset.grMnavTo);
        if (!target) return;
        show(target);
        target.querySelector('.mnav__back')?.focus({ preventScroll: true });
        return;
      }
      const back = e.target.closest('[data-gr-mnav-back]');
      if (back) {
        const from = back.closest('.mnav__panel');
        const target = document.getElementById(back.dataset.grMnavBack);
        if (!target) return;
        show(target);
        target.querySelector(`[data-gr-mnav-to="${from.id}"]`)?.focus({ preventScroll: true });
      }
    });
    nav.closest('[data-gr-layer]')?.addEventListener('gr:layer-open', () => show(rootPanel));
    show(rootPanel);
  };

  /* ── Hero slider (sections/gr-hero.liquid, 2+ slides) ────────────────────
     A manual, no-autoplay carousel. The track's own CSS (scroll-snap) does
     the actual scrolling — this only decides which slide is "current":
     marks every other slide `inert` (same pattern as the mobile nav panels
     above), keeps the dots and their aria-current in sync, and moves the
     track when an arrow, a dot or an arrow key asks to. A native swipe/drag
     scroll is picked up the same way as a click: on scroll end, whichever
     slide the track actually settled on becomes current. Until this runs,
     CSS hides every slide but the first (`data-gr-slider-ready` below) —
     there are no working arrows/dots without it anyway. */
  const sliders = new WeakMap();

  const setSlide = (slider, index, { scroll = true } = {}) => {
    const state = sliders.get(slider);
    if (!state) return;
    const { track, slides, dots } = state;
    index = Math.max(0, Math.min(index, slides.length - 1));
    state.index = index;
    slides.forEach((slide, i) => { slide.inert = i !== index; });
    dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    if (scroll) {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left: slides[index].offsetLeft, behavior: reduced ? 'auto' : 'smooth' });
    }
  };

  const moveSlide = (slider, delta) => {
    const state = sliders.get(slider);
    if (!state) return;
    setSlide(slider, (state.index + delta + state.slides.length) % state.slides.length);
  };

  const initHeroSlider = () => {
    document.querySelectorAll('[data-gr-slider]:not([data-gr-slider-ready])').forEach((slider) => {
      const track = slider.querySelector('[data-gr-slider-track]');
      const slides = track ? Array.from(track.children) : [];
      const dots = Array.from(slider.querySelectorAll('[data-gr-slider-dot]'));
      if (!track || slides.length < 2) return;
      sliders.set(slider, { track, slides, dots, index: 0 });
      slider.setAttribute('data-gr-slider-ready', '');
      setSlide(slider, 0, { scroll: false });

      let settle;
      track.addEventListener(
        'scroll',
        () => {
          clearTimeout(settle);
          settle = setTimeout(() => {
            const state = sliders.get(slider);
            if (!state) return;
            const at = state.slides.reduce(
              (best, slide, i) =>
                Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(state.slides[best].offsetLeft - track.scrollLeft) ? i : best,
              0
            );
            if (at !== state.index) setSlide(slider, at, { scroll: false });
          }, 120);
        },
        { passive: true }
      );
    });
  };

  document.addEventListener('click', (e) => {
    const slider = e.target.closest('[data-gr-slider-ready]');
    if (!slider) return;
    if (e.target.closest('[data-gr-slider-prev]')) moveSlide(slider, -1);
    else if (e.target.closest('[data-gr-slider-next]')) moveSlide(slider, 1);
    else {
      const dot = e.target.closest('[data-gr-slider-dot]');
      if (dot) setSlide(slider, Array.from(slider.querySelectorAll('[data-gr-slider-dot]')).indexOf(dot));
    }
  });
  document.addEventListener('keydown', (e) => {
    const slider = e.target.closest && e.target.closest('[data-gr-slider-ready]');
    if (!slider || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
    e.preventDefault();
    moveSlide(slider, e.key === 'ArrowRight' ? 1 : -1);
  });
  // Theme editor: selecting a slide's block brings that slide into view.
  document.addEventListener('shopify:block:select', (e) => {
    const slider = e.target.closest && e.target.closest('[data-gr-slider-ready]');
    const index = slider ? sliders.get(slider).slides.indexOf(e.target) : -1;
    if (index >= 0) setSlide(slider, index);
  });

  /* ── Newsletter result (sections/gr-newsletter.liquid) ───────────────────
     Shopify comes back from the customer form with ?customer_posted=true (or
     form_type=customer) and the form's #anchor. When the section rendered
     neither the thank-you nor an error (Shopify's form state didn't carry
     over, or the address is already subscribed), the URL is the store's
     word: the thank-you comes from the form's template. The result — the
     thank-you, or the refused field — is focused and brought into view once
     the page has loaded: the browser skips autofocus when the URL has an
     anchor, and its own jump to the anchor (at load) drops focus. */
  const showNewsletterResult = () => {
    const form = document.querySelector('form[data-gr-news]');
    if (!form) return;
    const params = new URLSearchParams(window.location.search);
    const posted = params.get('customer_posted') === 'true' || params.get('form_type') === 'customer';
    const targeted = window.location.hash === `#${form.id}`;
    if (!posted && !targeted) return;
    let result = form.querySelector('[data-gr-news-result]');
    const done = form.querySelector('template[data-gr-news-done]');
    if (!result && posted && done) {
      form.replaceChildren(done.content.cloneNode(true));
      result = form.querySelector('[data-gr-news-result]');
    }
    if (!result) return;
    const bringIn = () => {
      result.focus({ preventScroll: true });
      result.scrollIntoView({ block: 'center' });
    };
    if (document.readyState === 'complete') setTimeout(bringIn);
    else window.addEventListener('load', () => setTimeout(bringIn), { once: true });
  };

  /* ── Boot, and re-boot when the theme editor re-renders a section ──────── */
  const boot = () => {
    catnav.init();
    initMobileNav();
    initTabs();
    initHeroSlider();
    layer.wire();
  };
  boot();
  showNewsletterResult();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', (e) => {
    if (layer.current && e.target.contains(layer.current)) layer.close();
  });
})();
